import type { WorkDate } from "@yushi-portfolio/content-schema";
import type { Locale } from "./locale";

const EN_DASH = "–";

/** 2024 / 2024–2026 / 2024– */
export function formatWorkYear(date: WorkDate): string {
  if (date.ongoing) return `${date.start}${EN_DASH}`;
  if (date.end !== undefined && date.end !== date.start) return `${date.start}${EN_DASH}${date.end}`;
  return String(date.start);
}

/** 754 → "12分34秒" / "12 min 34 sec" */
export function formatDuration(totalSeconds: number, locale: Locale): string {
  const total = Math.round(totalSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const units: [number, string, string][] = [
    [hours, "時間", "h"],
    [minutes, "分", "min"],
    [seconds, "秒", "sec"],
  ];
  const parts = units.filter(([value]) => value > 0);
  if (parts.length === 0) return locale === "ja" ? "0秒" : "0 sec";
  return locale === "ja"
    ? parts.map(([value, ja]) => `${value}${ja}`).join("")
    : parts.map(([value, , en]) => `${value} ${en}`).join(" ");
}

/** "2024-05-12" → "2024.05.12"; precision is preserved ("2024-05" → "2024.05"). */
export function formatPartialDate(value: string): string {
  return value.replaceAll("-", ".");
}

/** Date range for exhibitions, omitting the repeated year: "2024.05.12–06.30". */
export function formatDateRange(start: string | undefined, end: string | undefined): string | undefined {
  if (!start) return end ? formatPartialDate(end) : undefined;
  if (!end || end === start) return formatPartialDate(start);
  const sameYear = start.slice(0, 4) === end.slice(0, 4) && end.length > 4;
  return `${formatPartialDate(start)}${EN_DASH}${formatPartialDate(sameYear ? end.slice(5) : end)}`;
}

export function yearOf(partialDate: string | undefined): number | undefined {
  return partialDate ? Number(partialDate.slice(0, 4)) : undefined;
}
